import * as React from 'react';
import {
  CHART_INK,
  CHART_MARGIN,
  defaultXLabel,
  formatCompact,
  labelStride,
  niceTicks,
  resolveSeriesColors,
  useMeasure,
  type ChartSeries,
  type TimePoint,
} from './chart-utils';
import { ChartLegend } from './chart-legend';
import { ChartTooltip, type ChartTooltipState } from './chart-tooltip';

export type TimeSeriesLineChartProps = {
  data: TimePoint[];
  series: ChartSeries[];
  height?: number;
  valueFormat?: (n: number) => string;
  xLabelFormat?: (bucket: string) => string;
  /** Fixed y range, e.g. [0, 100] for percentages. */
  yDomain?: [number, number];
  emptyLabel?: string;
  className?: string;
};

function valueAt(point: TimePoint, key: string): number | null {
  const raw = point[key];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

/** Multi-series 2px lines with a crosshair tooltip; single series gets an area wash. */
export function TimeSeriesLineChart({
  data,
  series,
  height = 240,
  valueFormat = formatCompact,
  xLabelFormat = defaultXLabel,
  yDomain,
  emptyLabel = 'No data',
  className,
}: TimeSeriesLineChartProps) {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const [hover, setHover] = React.useState<number | null>(null);
  const colors = resolveSeriesColors(series);

  const plotW = Math.max(0, width - CHART_MARGIN.left - CHART_MARGIN.right);
  const plotH = Math.max(0, height - CHART_MARGIN.top - CHART_MARGIN.bottom);

  const maxValue = React.useMemo(() => {
    let max = 0;
    for (const point of data) {
      for (const s of series) {
        const v = valueAt(point, s.key);
        if (v !== null) max = Math.max(max, v);
      }
    }
    return max;
  }, [data, series]);

  const ticks = yDomain ? niceTicks(yDomain[1]) : niceTicks(maxValue);
  const yMin = yDomain ? yDomain[0] : 0;
  const yMax = yDomain ? yDomain[1] : ticks[ticks.length - 1] || 1;
  const yScale = (v: number) => plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;
  const xScale = (i: number) => (data.length <= 1 ? plotW / 2 : (i / (data.length - 1)) * plotW);
  const stride = labelStride(data.length, plotW);

  // A missing value (null / NaN) breaks the line instead of drawing a zero.
  const paths = series.map((s) => {
    let d = '';
    let pen = false;
    data.forEach((p, i) => {
      const v = valueAt(p, s.key);
      if (v === null) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${xScale(i).toFixed(1)},${yScale(v).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  });
  const complete = series.every((s) => data.every((p) => valueAt(p, s.key) !== null));
  const lastIndexOf = (key: string) => {
    for (let i = data.length - 1; i >= 0; i -= 1) if (valueAt(data[i], key) !== null) return i;
    return -1;
  };

  const handleMove = (e: React.MouseEvent<SVGRectElement>) => {
    if (data.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = data.length <= 1 ? 0 : Math.round((x / Math.max(1, plotW)) * (data.length - 1));
    setHover(Math.max(0, Math.min(data.length - 1, idx)));
  };

  const tooltip: ChartTooltipState | null =
    hover !== null && data[hover]
      ? {
          x: CHART_MARGIN.left + xScale(hover),
          y: CHART_MARGIN.top,
          title: xLabelFormat(data[hover].bucket),
          rows: series.map((s, i) => {
            const v = valueAt(data[hover], s.key);
            return { label: s.label, value: v === null ? '—' : valueFormat(v), color: colors[i] };
          }),
        }
      : null;

  return (
    <div className={['w-full', className].filter(Boolean).join(' ')}>
      <ChartLegend series={series} className="mb-2" />
      <div ref={ref} className="relative w-full" style={{ height }} data-testid="line-chart">
        {data.length === 0 ? (
          <div
            className="flex h-full items-center justify-center text-sm"
            style={{ color: CHART_INK.muted }}
          >
            {emptyLabel}
          </div>
        ) : (
          <svg width={width} height={height} role="img" onMouseLeave={() => setHover(null)}>
            <g transform={`translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`}>
              {ticks.map((t) => (
                <g key={t} transform={`translate(0,${yScale(t)})`}>
                  <line x1={0} x2={plotW} stroke={CHART_INK.grid} strokeWidth={1} />
                  <text
                    x={-8}
                    dy="0.32em"
                    textAnchor="end"
                    fontSize={11}
                    fill={CHART_INK.muted}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {valueFormat(t)}
                  </text>
                </g>
              ))}
              {series.length === 1 && data.length > 1 && complete ? (
                <path
                  d={`${paths[0]} L${xScale(data.length - 1).toFixed(1)},${plotH} L${xScale(0).toFixed(1)},${plotH} Z`}
                  fill={colors[0]}
                  opacity={0.1}
                />
              ) : null}
              {series.map((s, i) => (
                <path
                  key={s.key}
                  data-testid="line"
                  d={paths[i]}
                  fill="none"
                  stroke={colors[i]}
                  strokeWidth={2}
                  strokeDasharray={s.dashed ? '6 4' : undefined}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
              {series.map((s, i) => {
                const end = lastIndexOf(s.key);
                return end >= 0 ? (
                  <circle
                    key={`end-${s.key}`}
                    cx={xScale(end)}
                    cy={yScale(valueAt(data[end], s.key) ?? 0)}
                    r={4}
                    fill={colors[i]}
                    stroke={CHART_INK.surface}
                    strokeWidth={2}
                  />
                ) : null;
              })}
              {hover !== null && data[hover] ? (
                <g>
                  <line
                    x1={xScale(hover)}
                    x2={xScale(hover)}
                    y1={0}
                    y2={plotH}
                    stroke={CHART_INK.muted}
                    strokeWidth={1}
                  />
                  {series.map((s, i) => {
                    const v = valueAt(data[hover], s.key);
                    return v === null ? null : (
                      <circle
                        key={`hover-${s.key}`}
                        cx={xScale(hover)}
                        cy={yScale(v)}
                        r={4}
                        fill={colors[i]}
                        stroke={CHART_INK.surface}
                        strokeWidth={2}
                      />
                    );
                  })}
                </g>
              ) : null}
              {data.map((p, i) =>
                i % stride === 0 ? (
                  <text
                    key={p.bucket}
                    x={xScale(i)}
                    y={plotH + 18}
                    textAnchor="middle"
                    fontSize={11}
                    fill={CHART_INK.muted}
                  >
                    {xLabelFormat(p.bucket)}
                  </text>
                ) : null,
              )}
              <line x1={0} x2={plotW} y1={plotH} y2={plotH} stroke={CHART_INK.grid} strokeWidth={1} />
              <rect
                x={0}
                y={0}
                width={plotW}
                height={plotH}
                fill="transparent"
                onMouseMove={handleMove}
                onMouseEnter={handleMove}
              />
            </g>
          </svg>
        )}
        <ChartTooltip state={tooltip} containerWidth={width} />
      </div>
    </div>
  );
}
